/**
 * DAC customization: keep Afterpay On-Site Messaging (`square-placement`) in sync when
 * the selected variant changes. The app block freezes `data-amount` at first render;
 * Pebble only morphs `product-price`, so messaging would otherwise stay on the initial price.
 */
import { ThemeEvents } from "@theme/events";

const PLACEMENT_SELECTOR = 'square-placement[data-amount], [id*="afterpay_on_site_messaging"] square-placement';

/**
 * Convert Shopify cents to the dollar string Afterpay expects on `data-amount`.
 * @param {number} priceInCents
 * @returns {string | null}
 */
function formatAfterpayAmount(priceInCents) {
  if (typeof priceInCents !== "number" || !Number.isFinite(priceInCents) || priceInCents < 0) {
    return null;
  }

  return String(+(priceInCents / 100).toFixed(2));
}

/**
 * Update a Square placement and force messaging to re-render.
 * @param {Element} placement
 * @param {string} amount
 */
function applyAmount(placement, amount) {
  if (!(placement instanceof HTMLElement)) return;
  if (placement.getAttribute("data-amount") === amount) return;

  placement.setAttribute("data-amount", amount);

  // Square messaging does not reliably react to attribute changes alone; replace the node.
  const clone = placement.cloneNode(true);
  if (clone instanceof HTMLElement) {
    clone.setAttribute("data-amount", amount);
  }
  placement.replaceWith(clone);
}

/**
 * @param {Event & { detail?: { resource?: { price?: number } } }} event
 */
function onVariantUpdate(event) {
  // Theme VariantUpdateEvent extends Event (not CustomEvent) but still carries `detail`.
  const variant = event.detail?.resource;
  const amount = formatAfterpayAmount(variant?.price);
  if (!amount) return;

  // Skip product cards — Afterpay app blocks live on the PDP product section.
  if (event.target instanceof Element && event.target.closest("product-card") && !event.target.closest("dialog")) {
    return;
  }

  const section =
    (event.target instanceof Element && event.target.closest(".shopify-section, dialog")) ||
    document.querySelector("main .shopify-section");

  if (!section) return;

  section.querySelectorAll(PLACEMENT_SELECTOR).forEach((placement) => {
    applyAmount(placement, amount);
  });
}

document.addEventListener(ThemeEvents.variantUpdate, onVariantUpdate);
